import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Euro } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "../../lib/api";

const PERIODS = [[7, "7 jours"], [30, "30 jours"], [90, "3 mois"], [365, "12 mois"], [0, "Depuis le début"]];
const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

function buckets(days, payments) {
  const now = new Date();
  const monthly = days === 0 || days > 90;
  const step = days === 90 ? 7 : 1;
  const out = [];
  if (monthly) {
    const first = payments.length ? new Date(payments[payments.length - 1].paid_at) : now;
    let d = days === 0 ? new Date(first.getFullYear(), first.getMonth(), 1) : new Date(now.getFullYear(), now.getMonth() - 11, 1);
    while (d <= now) {
      const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      out.push({ from: d, to: next, label: d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }) });
      d = next;
    }
  } else {
    const n = Math.ceil(days / step);
    for (let i = n - 1; i >= 0; i--) {
      const from = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - i * step - (step - 1)));
      const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + step);
      out.push({ from, to, label: from.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) });
    }
  }
  return out.map((b) => ({ label: b.label, total: payments.filter((p) => { const t = new Date(p.paid_at); return t >= b.from && t < b.to; }).reduce((s, p) => s + p.amount, 0) }));
}

function Delta({ total, prev }) {
  if (prev == null) return null;
  if (!prev) return <span className="text-xs text-ink-muted">{total ? "Nouvelle période d'encaissement ✨" : "Aucun encaissement sur la période précédente"}</span>;
  const pct = Math.round(((total - prev) / prev) * 100);
  const up = pct >= 0;
  const I = up ? TrendingUp : TrendingDown;
  return <span data-testid="revenue-delta" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${up ? "bg-bordeaux-tint text-bordeaux" : "bg-cream text-ink-muted"}`}><I size={13} /> {up ? "+" : ""}{pct}% vs période précédente ({eur(prev)})</span>;
}

export default function RevenueCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get(`/admin/revenue?days=${days}`).then(({ data }) => setData(data)).catch(() => setData({ total: 0, count: 0, payments: [] })); }, [days]);
  const series = useMemo(() => (data ? buckets(days, data.payments) : []), [data, days]);
  return (
    <section data-testid="revenue-card" className="rounded-[24px] border border-line bg-white p-5 text-ink shadow-card sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bordeaux-tint text-bordeaux"><Euro size={18} /></span>
          <div><h2 className="font-sans text-lg font-medium text-ink">Chiffre d'affaires encaissé</h2><p className="text-xs font-light text-ink-muted">Calculé à partir des versements marqués « reçu »</p></div>
        </div>
        <div className="flex flex-wrap gap-1 rounded-full bg-cream p-1 ring-1 ring-line">
          {PERIODS.map(([d, l]) => (
            <button key={d} data-testid={`revenue-period-${d}`} onClick={() => setDays(d)} className={`rounded-full px-3 py-1.5 text-xs transition-colors ${days === d ? "bg-bordeaux font-medium text-white" : "text-ink-soft hover:text-bordeaux"}`}>{l}</button>
          ))}
        </div>
      </div>
      {!data ? <p className="mt-5 text-sm text-ink-muted">Chargement…</p> : <div className="mt-5 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-1">
          <motion.p key={`${days}-${data.total}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="revenue-total" className="font-sans text-3xl font-medium text-ink">{eur(data.total)}</motion.p>
          <p data-testid="revenue-count" className="pb-1 text-sm text-ink-muted">{data.count} versement{data.count > 1 ? "s" : ""} encaissé{data.count > 1 ? "s" : ""}</p>
        </div>
        <div className="mt-2"><Delta total={data.total} prev={data.previous_total} /></div>
        <div data-testid="revenue-chart" className="mt-4 h-40 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(45,24,30,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#8C7A7F", fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={18} />
              <YAxis tick={{ fill: "#8C7A7F", fontSize: 11 }} axisLine={false} tickLine={false} width={64} tickFormatter={(v) => `${v.toLocaleString("fr-FR")}€`} />
              <Tooltip cursor={{ fill: "rgba(123,17,58,0.05)" }} contentStyle={{ background: "#FFFCF6", border: "1px solid #EADFD8", borderRadius: 14, color: "#2D181E", fontSize: 12 }} formatter={(v) => [eur(v), "Encaissé"]} />
              <Bar dataKey="total" fill="#7B113A" radius={[6, 6, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        </div>
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-ink-muted">Derniers encaissements</p>
          {data.payments.length === 0 && <p data-testid="revenue-empty" className="mt-3 rounded-2xl bg-cream p-4 text-sm font-light text-ink-muted">Aucun versement reçu sur cette période.</p>}
          <div className="mt-3 space-y-2">
            {data.payments.slice(0, 4).map((p, n) => (
              <button key={n} data-testid={`revenue-payment-${n}`} onClick={() => navigate(`/admin/clientes/${p.client_id}`)} className="flex w-full items-center justify-between gap-3 rounded-xl bg-cream/70 px-3.5 py-2.5 text-left transition-colors hover:bg-bordeaux-tint/50">
                <span className="min-w-0"><span className="block truncate text-sm text-ink">{p.client_name || "Cliente"}</span><span className="text-xs text-ink-muted">{p.label} · {new Date(p.paid_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}</span></span>
                <span className="shrink-0 text-sm font-medium text-bordeaux">{eur(p.amount)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>}
    </section>
  );
}
